import { Box, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export type ContentSalesDatum = {
  name: string;
  value: number;
  percent: number;
  amount: number;
};

function ContentSalesCustomTooltip({
  active,
  payload,
  data,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value?: number }>;
  data: ContentSalesDatum[];
}) {
  if (!active || !payload?.length) return null;
  return (
    <Paper
      elevation={8}
      sx={{
        p: 1.5,
        minWidth: 140,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600 }}>
        매출
      </Typography>
      {payload.map((entry) => {
        const item = data.find((d) => d.name === entry.name);
        if (!item) return null;
        return (
          <Typography key={entry.name} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <span style={{ color: '#666' }}>{entry.name}</span>
            <span style={{ fontWeight: 600 }}>{Number(item.percent).toFixed(1)}% / {item.amount.toLocaleString('ko-KR')}원</span>
          </Typography>
        );
      })}
    </Paper>
  );
}

const COLORS = [
  '#1E40AF',
  '#3B82F6',
  '#60A5FA',
  '#93C5FD',
  '#7d98beff',
];
type Props = {
  data?: ContentSalesDatum[];
};

/** 콘텐츠별 매출 */
export default function RouletteRevenueContentSalesChart({ data = [] }: Props) {
  const stackRow = data.reduce(
    (acc, d) => ({ ...acc, [d.name]: d.percent }),
    { name: '매출' } as Record<string, string | number>
  );

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 440,
        minHeight: 300,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 3,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: '0.95rem' }} color="#424242">
        콘텐츠별 매출
      </Typography>
      <Box sx={{ flex: 1, minHeight: 220, display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 0.5 }}>
        <ResponsiveContainer width="95%" height={220}>
          <BarChart
            layout="vertical"
            data={[stackRow]}
            margin={{ top: 12, right: 28, left: 12, bottom: 12 }}
          >
            <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={0} hide />
            <Tooltip
              cursor={false}
              content={<ContentSalesCustomTooltip data={data} />}
            />
            <Legend
              layout="vertical"
              align="left"
              verticalAlign="middle"
              wrapperStyle={{ paddingRight: 12, paddingLeft: 8, fontSize: 13 }}
              formatter={(value) => {
                const item = data.find((d) => d.name === value);
                const percent = item?.percent ?? 0;
                const amount = item?.amount ?? 0;
                const pctText = Number(percent).toFixed(1);
                return `${value} - ${pctText}% | ${amount.toLocaleString('ko-KR')}원`;
              }}
              iconType="square"
              iconSize={10}
            />
            {data.map((d, index) => (
              <Bar
                key={d.name}
                dataKey={d.name}
                stackId="a"
                fill={COLORS[index % COLORS.length]}
                radius={index === data.length - 1 ? [0, 4, 4, 0] : 0}
                barSize={28}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
